import {
  api,
  CustomNextMonitoringDateOptions,
  GetCustomNextMonitoringDateOptionsParams,
  MonitoringDate,
  PagedResultOfSpeakingMonitoring,
  SaveSpeakingMonitoringFollowUpRequest,
  SaveSpeakingMonitoringRequest,
  SpeakingMonitoringDetails,
  SpeakingMonitoringFollowUpResult,
  SpeakingMonitoringNextDateOptions,
  SpeakingMonitoringResult,
  SpeakingMonitoringStage,
} from 'api';
import { rtkQueryApi } from 'store/rtkQueryApi';
import { applyOptimisticUpdate } from 'store/rtkQueryUtils';

const speakingMonitoringApi = rtkQueryApi.injectEndpoints({
  endpoints: (builder) => ({
    speakingMonitoringList: builder.query<
      PagedResultOfSpeakingMonitoring,
      { examinerId: number; pageIndex: number; pageSize: number; showDeleted?: boolean }
    >({
      query: ({ examinerId, pageIndex, pageSize, showDeleted }) => {
        return api.speakingMonitoring.getExaminerSpeakingMonitorings({
          examinerId,
          page: pageIndex + 1,
          count: pageSize,
          'filter.showDeleted': showDeleted,
        });
      },
      providesTags: ['SpeakingMonitoring'],
    }),
    speakingNextMonitoringDate: builder.query<MonitoringDate, { examinerId: number }>({
      query: ({ examinerId }) => {
        return api.speakingMonitoring.getNextMonitoringDate({
          examinerId,
        });
      },
      providesTags: ['SpeakingMonitoring'],
    }),
    canAddSpeakingMonitoring: builder.query<boolean, { examinerId: number }>({
      query: ({ examinerId }) => {
        return api.speakingMonitoring.canAddSpeakingMonitoring({
          examinerId,
        });
      },
      providesTags: ['SpeakingMonitoring'],
    }),
    speakingMonitoring: builder.query<
      SpeakingMonitoringDetails,
      { id: number; examinerId: number }
    >({
      query: ({ id, examinerId }) => {
        return api.speakingMonitoring.get({
          examinerId,
          speakingMonitoringId: id,
        });
      },
      providesTags: ['SpeakingMonitoring'],
    }),
    calculateSpeakingMonitoringStage: builder.query<
      SpeakingMonitoringStage,
      { examinerId: number; date: string }
    >({
      query: ({ examinerId, date }) => {
        return api.speakingMonitoring.getCalculatedMonitoringStage({
          examinerId,
          date,
        });
      },
      providesTags: ['SpeakingMonitoring'],
    }),
    calculateFinalResult: builder.query<
      SpeakingMonitoringResult,
      {
        proceduresResult?: SpeakingMonitoringResult | null;
        ratingResult?: SpeakingMonitoringResult | null;
      }
    >({
      query: (request) => {
        return api.speakingMonitoringResult.calculateFinalResult(request);
      },
    }),
    calculateSpeakingNextMonitoringDate: builder.query<
      SpeakingMonitoringNextDateOptions[],
      //TODO: this is workaround for a typing issue in the api - we should add dedicated model for create
      Omit<
        SpeakingMonitoringDetails,
        | 'isDeleted'
        | 'deletedBy'
        | 'archivedBy'
        | 'finalResultName'
        | 'speakingCertificationId'
        | 'otherReason'
      >
    >({
      query: (monitoringDetails) => {
        return api.speakingMonitoring.getCalculatedNextMonitoringDate(
          monitoringDetails as SpeakingMonitoringDetails
        );
      },
      providesTags: ['SpeakingMonitoring'],
    }),
    getCustomNextMonitoringDateOptions: builder.query<
      CustomNextMonitoringDateOptions,
      GetCustomNextMonitoringDateOptionsParams
    >({
      query: (params) => {
        return api.speakingMonitoring.getCustomNextMonitoringDateOptions(params);
      },
    }),
    canAddSpeakingMonitoringFollowUp: builder.query<
      boolean,
      { examinerId: number; speakingMonitoringId: number }
    >({
      query: ({ examinerId, speakingMonitoringId }) => {
        return api.speakingMonitoring.canAddFollowUp({
          examinerId,
          speakingMonitoringId,
        });
      },
      providesTags: ['SpeakingMonitoring'],
    }),
    getSpeakingMonitoringFollowUp: builder.query<
      SpeakingMonitoringFollowUpResult,
      { examinerId: number; speakingMonitoringId: number }
    >({
      query: ({ examinerId, speakingMonitoringId }) => {
        return api.speakingMonitoring.getFollowUp({
          examinerId,
          speakingMonitoringId,
        });
      },
      providesTags: ['SpeakingMonitoring'],
    }),
    createSpeakingMonitoringFollowUp: builder.mutation<
      number,
      {
        examinerId: number;
        speakingMonitoringId: number;
        followUp: SaveSpeakingMonitoringFollowUpRequest;
      }
    >({
      query: ({ examinerId, speakingMonitoringId, followUp }) => {
        return api.speakingMonitoring.createFollowUp(
          { examinerId, speakingMonitoringId },
          followUp
        );
      },
      invalidatesTags: ['SpeakingMonitoring', 'ExaminerStatus'],
    }),
    updateSpeakingMonitoringFollowUp: builder.mutation<
      void,
      {
        examinerId: number;
        speakingMonitoringId: number;
        followUp: SaveSpeakingMonitoringFollowUpRequest;
      }
    >({
      query: ({ examinerId, speakingMonitoringId, followUp }) => {
        return api.speakingMonitoring.updateFollowUp(
          { examinerId, speakingMonitoringId },
          followUp
        );
      },
      invalidatesTags: ['SpeakingMonitoring', 'ExaminerStatus'],
    }),
    deleteSpeakingMonitoringFollowUp: builder.mutation<
      void,
      { examinerId: number; speakingMonitoringId: number }
    >({
      query: ({ examinerId, speakingMonitoringId }) => {
        return api.speakingMonitoring.deleteFollowUp({
          examinerId,
          speakingMonitoringId,
        });
      },
      onQueryStarted: async (
        { examinerId, speakingMonitoringId },
        { dispatch, queryFulfilled }
      ) => {
        // Optimistically clear followUpId so the follow-up query skips before tag invalidation refetches it (would 404).
        const patchResult = dispatch(
          speakingMonitoringApi.util.updateQueryData(
            'speakingMonitoring',
            { id: speakingMonitoringId, examinerId },
            (draft) => {
              draft.followUpId = null;
            }
          )
        );
        await applyOptimisticUpdate(patchResult, queryFulfilled);
      },
      invalidatesTags: ['SpeakingMonitoring', 'ExaminerStatus'],
    }),
    createSpeakingMonitoring: builder.mutation<
      number,
      {
        examinerId: number;
        monitoring: SaveSpeakingMonitoringRequest;
      }
    >({
      query: ({ examinerId, monitoring }) => {
        return api.speakingMonitoring.save({ examinerId }, monitoring);
      },
      invalidatesTags: ['SpeakingMonitoring', 'ExaminerStatus'],
    }),
    updateSpeakingMonitoring: builder.mutation<
      number,
      { examinerId: number; id: number; monitoring: SaveSpeakingMonitoringRequest }
    >({
      query: ({ examinerId, monitoring }) => {
        return api.speakingMonitoring.save({ examinerId }, monitoring);
      },
      invalidatesTags: ['SpeakingMonitoring', 'ExaminerStatus'],
    }),
    deleteSpeakingMonitoring: builder.mutation<void, { id: number; examinerId: number }>({
      query: ({ id, examinerId }) => {
        return api.speakingMonitoring.delete({
          examinerId,
          speakingMonitoringId: id,
        });
      },
      invalidatesTags: ['SpeakingMonitoring', 'ExaminerStatus'],
    }),
    restoreSpeakingMonitoring: builder.mutation<void, { id: number; examinerId: number }>({
      query: ({ id, examinerId }) => {
        return api.speakingMonitoring.restore({
          examinerId,
          speakingMonitoringId: id,
        });
      },
      invalidatesTags: ['SpeakingMonitoring', 'ExaminerStatus'],
    }),
  }),
});

export const {
  useSpeakingMonitoringListQuery,
  useSpeakingNextMonitoringDateQuery,
  useCanAddSpeakingMonitoringQuery,
  useSpeakingMonitoringQuery,
  useCalculateSpeakingMonitoringStageQuery,
  useLazyCalculateFinalResultQuery,
  useCalculateSpeakingNextMonitoringDateQuery,
  useGetCustomNextMonitoringDateOptionsQuery,
  useCanAddSpeakingMonitoringFollowUpQuery,
  useGetSpeakingMonitoringFollowUpQuery,
  useCreateSpeakingMonitoringFollowUpMutation,
  useUpdateSpeakingMonitoringFollowUpMutation,
  useDeleteSpeakingMonitoringFollowUpMutation,
  useCreateSpeakingMonitoringMutation,
  useUpdateSpeakingMonitoringMutation,
  useDeleteSpeakingMonitoringMutation,
  useRestoreSpeakingMonitoringMutation,
} = speakingMonitoringApi;
